<template>
    <v-card class="pa-4">
        <v-dialog v-model="showValidationDialog">
            <v-card class="search-modal">
                <h2>Validation Results</h2>
                <v-form class="search-form">
                    <v-alert v-if="!!validationResult && !!validationResult.errors && validationResult.errors.length > 0" type="error">
                        <pre>{{ validationResult.errors.join("\r\n") }}</pre>
                    </v-alert>
                    <v-alert v-if="!!validationResult && validationResult.warnings && validationResult.warnings.length > 0" type="warning" outlined>
                        <pre>{{ validationResult.warnings.join("\r\n") }}</pre>
                    </v-alert>
                    <v-btn large
                           color="primary"
                           width="125px"
                           :disabled="!validationResult.canBeSaved"
                           v-on:click="saveChanges">
                        <v-icon v-if="!validationResult.canBeSaved">mdi-alert</v-icon>
                        Save
                    </v-btn>
                </v-form>
            </v-card>
        </v-dialog>
        <v-row class="mb-3">
            <h3 v-if="judgeInfo.judgeId">Judge Details - {{judgeInfo.judgeId}}</h3>
            <h3 v-else>Create New Judge</h3>
        </v-row>
        <br />
        <v-row>
            <v-alert width="100%" class="pr-2 pl-2" v-if="errorMessage" type="error"> {{ errorMessage }}</v-alert>
            <v-textarea :disabled="!isEditMode"
                        :visible="!isEditMode"
                        outlined
                        auto-grow
                        clearable
                        rows="1"
                        v-model="judgeInfo.adminNotes"
                        label="Description of changes (required)" />
            <v-btn class="ml-1 mr-1"      
                   :disabled="isEditMode && (!judgeInfo.adminNotes || /^\s*$/.test(judgeInfo.adminNotes))"
                   color="primary"
                   @click="toggleEditMode">
                {{ isEditMode ? "Save" : "Edit" }}
            </v-btn>
        </v-row>


        <v-row>
            <v-col>
                <v-card class="pa-2" elevation="2">
                    <v-card-title class="text-h6">
                        Judge Status &amp; Affiliation
                    </v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <v-checkbox v-model="judgeInfo.isActive"
                                            label="Active" :disabled="!isEditMode"></v-checkbox>
                                <v-text-field v-model="judgeInfo.affiliation"
                                              label="Affiliation" :rules="[v => (v || '' ).length <= 50 || 'Affiliate is limited to 50 chars']"
                                              :disabled="!isEditMode" />

                            </v-col>
                        </v-row>
                    </v-card-text>
                    <br /> <br /> <br />

                </v-card>

            </v-col>

            <v-col>
                <v-card class="pa-2" elevation="2">
                    <v-card-title class="text-h6">Packet Information &amp; Dates</v-card-title>
                    <v-card-text>
                        <v-row>
                            <v-col>
                                <v-checkbox v-model="judgeInfo.infoStatus" class="mr-3"
                                            label="Info Pack Sent" :disabled="!isEditMode"></v-checkbox>
                                <DatepickerFormatted 
                                            :disabled="!isEditMode"
                                            :date="judgeInfo.sentInfoDate"
                                            label="Sent Package Date"
                                            v-on:updateDates="updateDates($event, 'sentInfoDate')" />
                                <DatepickerFormatted 
                                            :disabled="!isEditMode"
                                            :date="judgeInfo.lastShowDate"
                                            label="Last Show Date"
                                            v-on:updateDates="updateDates($event, 'lastShowDate')" />

                            </v-col>
                        </v-row>

                    </v-card-text>
                </v-card>

            </v-col>
        </v-row>

       
            <div class="mt-3">
                <v-row>

                    <v-col>
                        <h3 class="mb-4">Judging Approvals</h3>

                        <v-data-table dense class="elevation-1"
                                      :headers="headers"
                                      :items="judgeInfo.judgingApprovals"
                                      :items-per-page="5">
                            <template v-slot:[`item.emergencyStatus`]="{ item }">
                                <font-awesome-icon v-if="item.emergencyStatus !== null"
                                                   :icon="item.emergencyStatus ? 'check' : 'times'"
                                                   :color="item.emergencyStatus ? 'green' : 'red'" />
                                <span v-else></span>
                            </template>
                            <template v-slot:[`item.testStatus`]="{ item }">
                                <font-awesome-icon v-if="item.testStatus !== null"
                                                   :icon="item.testStatus ? 'check' : 'times'"
                                                   :color="item.testStatus ? 'green' : 'red'" />
                                <span v-else>
                                    <font-awesome-icon icon="times" color="red"></font-awesome-icon>
                                </span>
                            </template>
                            <template v-slot:[`item.approvalDate`]="{ item }">
                                <localized-date v-if="item.approvalDate" :date="item.approvalDate" />

                            </template>
                            <template v-slot:[`item.testDate`]="{ item }">
                                <localized-date v-if="item.testDate" :date="item.testDate" />

                            </template>
                            <template v-slot:[`item.isActive`]="{ item }">
                                {{ item.isActive ? 'Active' : 'Inactive'}}
                            </template>                                                      

                            <template v-slot:top>
                                <v-toolbar flat>
                                    <v-spacer></v-spacer>
                                    <v-dialog v-model="dialog"
                                              max-width="500px">
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-btn color="primary"
                                                   dark
                                                   :disabled="!isEditMode"
                                                   class="mb-2"
                                                   v-bind="attrs"
                                                   v-on="on">
                                                Add Approval Entry
                                            </v-btn>
                                        </template>
                                        <v-card>
                                            <v-card-title>
                                                <span class="text-h5">{{ formTitle }}</span>
                                            </v-card-title>

                                            <v-card-text>
                                                <v-container>
                                                    <v-form v-model="isValid">
                                                        <v-row>
                                                            <v-select v-model="editedItem.showTypeId"
                                                                      :items="showTypeIds" class="mr-2"
                                                                      label="Show Type*"
                                                                      :rules="[(v) => !!v || 'Show Type is required',]"></v-select>
                                                        </v-row>
                                                        <v-row>
                                                            <v-select v-model="editedItem.classId"
                                                                      :items="classTypeIds" class="mr-2"
                                                                      label="Class"></v-select>
                                                        </v-row>
                                                        <v-row>
                                                            <DatepickerFormatted
                                                                        :date="editedItem.approvalDate"
                                                                        label="Approval Date"
                                                                        v-on:updateDates="updateEditedItemDates($event, 'approvalDate')" />
                                                        </v-row>
                                                        <v-row>
                                                            <v-checkbox v-model="editedItem.emergencyStatus" class="mr-2"
                                                                        label="Emergency?"></v-checkbox>
                                                        </v-row>
                                                        <v-row>
                                                            <v-checkbox v-model="editedItem.testStatus" class="mr-2"
                                                                        label="Tested?"></v-checkbox>
                                                        </v-row>
                                                        <v-row>
                                                            <DatepickerFormatted
                                                                        :date="editedItem.testDate"
                                                                        label="Test Date"
                                                                        v-on:updateDates="updateEditedItemDates($event, 'testDate')" />

                                                        </v-row>
                                                        <v-row>
                                                            <v-text-field v-model="editedItem.testScore" label="Test Score" :rules="[isValidNumber]" />
                                                        </v-row>
                                                        <v-row>
                                                            <v-checkbox v-model="editedItem.isActive" label="Active" />
                                                        </v-row>
                                                    </v-form>
                                                </v-container>
                                            </v-card-text>

                                            <v-card-actions v-if="isEditMode">
                                                <v-spacer></v-spacer>
                                                <v-btn color="blue darken-1"
                                                       text
                                                       @click="close">
                                                    Cancel
                                                </v-btn>
                                                <v-btn color="blue darken-1"
                                                       text
                                                       :disabled="!isValid"
                                                       @click="save">
                                                    Add/Update
                                                </v-btn>
                                            </v-card-actions>
                                        </v-card>
                                    </v-dialog>
                                    <v-dialog v-model="dialogDelete" max-width="500px">
                                        <v-card>
                                            <v-card-title class="text-h5">Are you sure you want to delete this item?</v-card-title>
                                            <v-card-actions>
                                                <v-spacer></v-spacer>
                                                <v-btn color="blue darken-1" text @click="closeDelete">Cancel</v-btn>
                                                <v-btn color="blue darken-1" text @click="deleteItemConfirm">OK</v-btn>
                                                <v-spacer></v-spacer>
                                            </v-card-actions>
                                        </v-card>
                                    </v-dialog>
                                </v-toolbar>
                            </template>
                            <template v-slot:item.actions="{ item }">
                                <v-icon small v-if="isEditMode"
                                        class="mr-2"
                                        @click="editItem(item)">
                                    mdi-pencil
                                </v-icon>
                                <v-icon small v-if="isEditMode"
                                        @click="deleteItem(item)">
                                    mdi-delete
                                </v-icon>
                            </template>

                        </v-data-table>
                    </v-col>
                </v-row>
            </div>

            <div class="mt-3">
                <v-divider class="my-3"></v-divider>
                <v-row class="pt-0 mt-0">
                    <v-col>
                        <h3>Notes</h3>
                    </v-col>

                </v-row>

                <v-row>

                    <v-textarea
                        :disabled="!isEditMode"
                        v-model="judgeInfo.notes"
                        outlined
                        rows="1"
                        auto-grow
                    />

                </v-row>
            </div>
        <v-snackbar v-model="showInfoMessage" title color="green">
            {{ infoMessage }}
            <template v-slot:action="{ attrs }">
            <v-btn color="primary" text v-bind="attrs" @click="showInfoMessage = false">
                Close
            </v-btn>
            </template>
        </v-snackbar>
        <v-snackbar v-model="showErrorMessage" title color="red">
            {{ errorMessage }}
            <template v-slot:action="{ attrs }">
            <v-btn color="primary" text v-bind="attrs" @click="showErrorMessage = false">
                Close
            </v-btn>
            </template>
        </v-snackbar>
</v-card>
</template>

<script lang="ts">
import Vue from "vue";
import DatepickerFormatted from "../../common/inputs/DatepickerFormatted.vue";

import judgeService from "@/services/admin/judgeService";
import showService from "@/services/admin/showService";

import { Messages } from "@/constants";
import { ShowType } from '@/types/admin/Show';
import { JudgeInfo, JudgingApprovals, ClassType } from '@/types/admin/Judge';

export default Vue.extend({
    name: "JudgeDetails",
    components: {
        DatepickerFormatted,
    },
    data: function () {
        return {                
            judgeInfo: {
                personId: this.$props.personId,
                judgingApprovals: [] as JudgingApprovals[]
            } as JudgeInfo,
            isEditMode: false,
            showValidationDialog: false,
            validationResult: {} as any,
            showTypes: [] as ShowType[],
            classTypes: [] as ClassType[],
            editedIndex: -1,
            editedItem: { isActive: true } as any,
            defaultItem: { isActive: true } as any,
            dialog: false,
            dialogDelete: false,
            showTypeIds: [] as string[],
            classTypeIds: [] as string[],
            reloadPage: false,
            isValid: true,
            headers: [
                { text: "Show Type", align: "left", value: "showTypeId" },
                { text: "Class", align: "left", value: "classId" },
                { text: "Emergency?", align: "left", value: "emergencyStatus" },
                { text: "Approval Date", align: "left", value: "approvalDate" },
                { text: "Tested?", align: "left", value: "testStatus" },
                { text: "Test Date", align: "left", value: "testDate" },
                { text: "Test Score", align: "left", value: "testScore" },
                { text: "Active/Inactive", align: "left", value: "isActive" },
                { text: 'Actions', value: 'actions', sortable: false },
            ],
            errorMessage: '',
            infoMessage: '',
            showErrorMessage: false,
            showInfoMessage: false               
        };
    },
    created: function () {
        if (this.personId) {
            judgeService.getJudgeById(this.personId).then((res: JudgeInfo) => {
                if (res) {
                    
                    this.judgeInfo = res;
                    this.reloadPage = false;
                } else {
                    this.judgeInfo = {
                        personId: this.$props.personId,
                        judgingApprovals: [] as JudgingApprovals[]
                    } as JudgeInfo,
                    this.isEditMode = true;
                    this.reloadPage = true;
                }
            }).catch(e => {
                this.judgeInfo = {
                        personId: this.$props.personId,
                        judgingApprovals: [] as JudgingApprovals[]
                    } as JudgeInfo,
                this.reloadPage = true;
            })
        }
        else {
            this.judgeInfo = {
                personId: this.$props.personId,
                judgingApprovals: [] as JudgingApprovals[]
            } as JudgeInfo,
            this.reloadPage = true;
        }
        showService.getJudgeShowTypes().then((res: any) => {
            this.showTypes = res;
            for (let i = 0; i < this.showTypes.length; i++) {
                this.showTypeIds.push(this.showTypes[i].showTypeId);
            }
            
        });

        judgeService.getJudgeClassTypes().then((res: any) => {
            this.classTypes = res;
            this.classTypeIds.push('');
            for (let i = 0; i < this.classTypes.length; i++) {
                this.classTypeIds.push(this.classTypes[i].classId);
            }

        });

    },
    watch: {
        personId: function (newVal: String) {
            judgeService
                .getJudgeById(this.personId)
                .then((res) => {
                    if(res){
                        this.judgeInfo = res;
                    } else {
                        this.judgeInfo = {
                            personId: this.$props.personId,
                            judgingApprovals: [] as JudgingApprovals[]
                        } as JudgeInfo;
                    }
                });
        },
        dialog(val) {
            val || this.close()
        },
        dialogDelete(val) {
            val || this.closeDelete()
        },
    },
    computed: {
        formTitle() {
            return this.$data.editedIndex === -1 ? "Add Approval" : "Edit Approval";
        },
    },
    methods: {
        reloadData: function () {
            if (this.personId) {
                judgeService
                    .getJudgeById(this.personId)
                    .then((res) => {
                        if(res){
                            this.judgeInfo = res;
                        } else {
                            this.judgeInfo = {
                                personId: this.$props.personId,
                                judgingApprovals: [] as JudgingApprovals[]
                            } as JudgeInfo;
                        };
                    })
                    .catch((e) => {
                        this.judgeInfo = {
                            personId: this.$props.personId,
                            judgingApprovals: [] as JudgingApprovals[]
                        } as JudgeInfo
                    });
            }
            else {
                this.judgeInfo = {} as JudgeInfo;
                this.isEditMode = true;
            }
            this.showValidationDialog = false;
            this.errorMessage = '';
            this.showErrorMessage = false;
            this.validationResult = {};
            
        },
        saveChanges: function () {
            this.errorMessage = "";
            this.showValidationDialog = false;
            this.showErrorMessage = false;
            this.$store.dispatch("changeLoaderStatus", true);
            this.judgeInfo.personId = this.$props.personId; 
            judgeService
                .editJudge(this.judgeInfo)
                .then((res) => {
                    if (res.success) {
                        this.reloadData();
                        if (this.reloadPage) {
                            window.location.reload();
                        } 
                        this.infoMessage = Messages.SaveSuccessful;
                        this.showInfoMessage = true;
                    } else {              
                        this.errorMessage = Messages.SaveError + " " + res.messages.join(', ');
                        this.showErrorMessage = true;
                    }
                })
                .catch((e) => {                  
                    this.errorMessage = Messages.SaveError;
                    this.showErrorMessage = true;
                })
                .finally(() => this.$store.dispatch("changeLoaderStatus", false));
        },
        updateDates: function (val: any, key: string) {
            this.$data.judgeInfo[`${key}Formatted`] = val.formattedDate;
            this.$data.judgeInfo[key] = val?.date;
            this.$data.judgeInfo = Object.assign({}, this.$data.judgeInfo);
        },
        updateEditedItemDates: function (val: any, key: string) {
            this.$data.editedItem[key] = val?.date;
            this.$data.editedItem = Object.assign({}, this.$data.editedItem);
        },
        updateText: function (key: string, event: string) {
            this.$data.judgeInfo[key] = event;
        },
        validateAndConfirmSavingChanges: function () {
            this.errorMessage = "";
            this.showErrorMessage = false;
            
            if (this.judgeInfo.affiliation) {
                if (this.judgeInfo.affiliation.length > 50) {
                    this.errorMessage = "Affiliation is too long.";
                    this.showErrorMessage = true;
                    this.isEditMode = false; //will toggle it back to edit mode
                    return;
                }
            }
            this.saveChanges();
            this.validationResult = {};
            //Right now there isn't anything to validate on the back end. 
            //Leaving the placeholders there in case we want to add some

        },
        toggleEditMode: function () {
            if (this.isEditMode) {
                this.validateAndConfirmSavingChanges();
            }
            this.isEditMode = !this.isEditMode;
        },
        
        editItem(item: JudgingApprovals) {
            this.editedIndex = this.judgeInfo.judgingApprovals.indexOf(item)
            this.editedItem = Object.assign({}, item)
            this.dialog = true
        },

        deleteItem(item: JudgingApprovals) {
            this.editedIndex = this.judgeInfo.judgingApprovals.indexOf(item)
            this.editedItem = Object.assign({}, item)
            this.dialogDelete = true
        },

        deleteItemConfirm() {
            this.judgeInfo.judgingApprovals.splice(this.editedIndex, 1)
            this.closeDelete()
        },

        close() {
            this.dialog = false
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.defaultItem)
                this.editedIndex = -1
            })
        },

        closeDelete() {
            this.dialogDelete = false
            this.$nextTick(() => {
                this.editedItem = Object.assign({}, this.defaultItem)
                this.editedIndex = -1
            })
        },

        save() {
            if (this.editedIndex > -1) {
                Object.assign(this.judgeInfo.judgingApprovals[this.editedIndex], this.editedItem)

            }
            else {
                try {
                    this.judgeInfo.judgingApprovals.push(this.editedItem);

                }
                catch (error) {
                    this.$handleError(error);
                    return;
                }


            }
            this.close();

        },
        isValidNumber(value: string) {
            if (!value) {
                return true;
            }
            if (!isNaN(parseFloat(value))) return true;

            return 'The value has to be numeric';

        },
    },
    props: {
        personId: { type: Number },
    },
});
</script>
